import { Card, Flex, Skeleton, Table, Typography } from "antd";
import type { TableProps } from "antd";
import { Link } from "react-router-dom";
import CardSectionTitle from "components/CardSectionTitle.component";
import VoucherSection from "components/vouchers/VoucherSection.component";
import { useCustomerReferral } from "hooks/queries/referral";
import { StatusBadge } from "pages/schedule/component/ScheduleStatusBadge";
import { th, tnum } from "shared/components/TableText";
import { PAGE_SIZE } from "shared/constants";
import PATHS from "shared/constants/paths";
import { translate } from "shared/languages";
import { User } from "shared/types";
import { ReferralEffectiveStatus, ReferralStatus, UserType } from "shared/types/enum";
import { CustomerReferralMadeRow } from "shared/types/referral";
import { isUserDeactivated, REFERRAL_STATUS_LABELS, REFERRAL_STATUS_TONE } from "shared/utils";
import { DEFAULT_DATE_FORMAT, formatDate } from "shared/utils/dayjs";
import { localizeRewardSummary } from "shared/utils/loyalty";
import { textPx } from "shared/constants/zoom";

const { Text } = Typography;

// Labels + tones live in shared/utils (REFERRAL_STATUS_*); "lapsed" is the
// derived past-deadline state and reads as Expired.
const statusBadgeFor = (status: string) => {
  const key = (
    status === ReferralStatus.Converted || status === ReferralStatus.Pending ? status : "lapsed"
  ) as ReferralEffectiveStatus;
  return <StatusBadge tone={REFERRAL_STATUS_TONE[key]} label={REFERRAL_STATUS_LABELS[key]} />;
};

// Customer detail -> Reward tab -> Referral pane: who referred this client,
// who this client has referred (with statuses), and the referral-sourced
// vouchers (same shared VoucherSection as loyalty).
const ReferralTab = ({ customer }: { customer: User }) => {
  const customerId = customer.id;
  const { data, isLoading } = useCustomerReferral(customerId);
  const isDeactivated = isUserDeactivated(customer, UserType.Customer);

  if (isLoading || !data) {
    return (
      <Card>
        <Skeleton active paragraph={{ rows: 5 }} />
      </Card>
    );
  }

  const { referred_by, referrals_made, vouchers } = data;

  const madeColumns: TableProps<CustomerReferralMadeRow>["columns"] = [
    {
      title: th(translate("referral.pending.col_client")),
      dataIndex: "referred_name",
      key: "referred_name",
      render: (name: string, row) => (
        <Link
          to={`${PATHS.CUSTOMER}/${row.referred_customer_id}?tab=5&reward=referral`}
        >
          <span style={{ fontWeight: 600 }}>{name}</span>
        </Link>
      ),
    },
    {
      title: th(translate("referral.pending.col_registered")),
      dataIndex: "created_at",
      key: "created_at",
      render: (d: string) => (
        <span style={tnum}>{formatDate(d, DEFAULT_DATE_FORMAT)}</span>
      ),
      sorter: (a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      defaultSortOrder: "descend",
    },
    {
      title: th(translate("referral.made.col_reward")),
      key: "reward",
      render: (_, row) =>
        row.reward_summary ? (
          <span>{localizeRewardSummary(row.reward_summary)}</span>
        ) : (
          "-"
        ),
    },
    {
      title: th(translate("label.status")),
      key: "status",
      render: (_, row) => statusBadgeFor(row.status),
    },
  ];

  return (
    <Flex vertical gap={16}>
      {/* ---- Referred by ---- */}
      <Card>
        <Flex vertical gap={14}>
          <CardSectionTitle
            title={translate("referral.referred_by.title")}
            subtitle={translate("referral.referred_by.subtitle")}
          />
          {referred_by ? (
            <Flex align="center" gap={12} wrap="wrap">
              <Link
                to={`${PATHS.CUSTOMER}/${referred_by.referrer_customer_id}?tab=5&reward=referral`}
              >
                <span style={{ fontWeight: 600 }}>
                  {referred_by.referrer_name}
                </span>
              </Link>
              {statusBadgeFor(referred_by.status)}
              <Text type="secondary" style={{ fontSize: textPx(13) }}>
                {translate("referral.pending.col_registered")}:{" "}
                {formatDate(referred_by.created_at, DEFAULT_DATE_FORMAT)}
                {referred_by.converted_at
                  ? ` · ${translate("referral.referred_by.converted_at")}: ${formatDate(
                      referred_by.converted_at,
                      DEFAULT_DATE_FORMAT
                    )}`
                  : referred_by.expires_at
                    ? ` · ${translate("referral.pending.col_expires")}: ${formatDate(
                        referred_by.expires_at,
                        DEFAULT_DATE_FORMAT
                      )}`
                    : ""}
              </Text>
            </Flex>
          ) : (
            <Text type="secondary">
              {translate("referral.referred_by.none")}
            </Text>
          )}
        </Flex>
      </Card>

      {/* ---- Referrals made ---- */}
      <Card>
        <Flex vertical gap={18}>
          <CardSectionTitle
            title={translate("referral.made.title")}
            subtitle={translate("referral.made.subtitle")}
          />
          <Table
            showSorterTooltip={false}
            rowKey="id"
            columns={madeColumns}
            dataSource={referrals_made}
            pagination={{ pageSize: PAGE_SIZE, showSizeChanger: false }}
            locale={{ emptyText: translate("referral.made.empty") }}
          />
        </Flex>
      </Card>

      {/* ---- Referral vouchers ---- */}
      <VoucherSection
        customerId={customerId}
        vouchers={vouchers}
        isDeactivated={isDeactivated}
        title={translate("referral.vouchers.title")}
        subtitle={translate("referral.vouchers.subtitle")}
        emptyText={translate("referral.vouchers.empty")}
      />
    </Flex>
  );
};

export default ReferralTab;
